import { useMessage, useMessagePartRuntime } from '@assistant-ui/react'

import type { ChatThreadMessageMetadata } from '@/components/chat-v2/hooks/useConvertChatMessage'
import { useStreamingPartText } from '@/components/chat-v2/providers/ChatStreamModelProvider'

/**
 * Live text for a streaming assistant part.
 *
 * Content tokens ping only their `part:` topic in the stream model — the
 * provider, repository, and converted `ThreadMessage` update on structural
 * events only. The converted part therefore carries text as of the last
 * structural event; while the part is running, this hook overlays the model's
 * always-current text so the visible stream never stalls between structural
 * events. Once the part completes, the next structural conversion catches the
 * part text up and the overlay unsubscribes.
 */
export function useLiveAssistantPartText(
  isRunningPart: boolean,
  fallbackText: string,
): string {
  const messageId = useMessage((state) => state.id)
  const streamPartIndexes = useMessage((state) => {
    // `metadata.custom` is typed as an open record by assistant-ui; our
    // converter is the only writer and stamps ChatThreadMessageMetadata.
    const custom = state.metadata.custom as ChatThreadMessageMetadata
    return custom.streamPartIndexes
  })
  const selector = useMessagePartRuntime().path.messagePartSelector
  const contentIndex = selector.type === 'index' ? selector.index : -1
  const modelPartIndex =
    isRunningPart && contentIndex >= 0
      ? (streamPartIndexes?.[contentIndex] ?? -1)
      : -1

  const liveText = useStreamingPartText(
    modelPartIndex >= 0 ? messageId : null,
    modelPartIndex,
  )
  const live = isRunningPart ? liveText : undefined
  // The overlay exists to LEAD the converted text between structural events.
  // A missing or shorter live read is cleared/stale model state (post-cancel
  // delta clearing, write-back, hydration) — regressing the rendered text
  // would trip StreamingMarkdownText's shrink-reset and re-animate history.
  return live !== undefined && live.length >= fallbackText.length
    ? live
    : fallbackText
}
